import { SKILL_NAMES } from 'js/src/agent/skills/skillNames';
import type { CodeoSkill } from '../types';
import { check, countOf } from '../skillUsage';

export const radialTickSystemSkill: CodeoSkill = {
    name: SKILL_NAMES.radialTickSystem,
    kind: 'codeo',
    description:
        'One parametric ring of radial ticks that becomes a sunburst, a gauge arc, a dial, a ' +
        'clock ring, a spiral, or an expanding field — geometry to build a graphic out of when ' +
        'the alternative is a card or a glowing orb.',
    documentUsesSkill: check(
        (script, markup) =>
            (/createElementNS/.test(script) && /rotate\(/.test(script)) ||
            countOf(markup, /rotate\(/g) >= 3,
    ),
    instructions: `# Radial Tick System

A ring of ticks around a centre is the most reparameterisable primitive in graphic design. Change the tick length and it is a sunburst; shorten it and it is a gauge; restrict the arc and it is a dial; space them out and it is a clock; grow the radius with the index and it is a spiral; tween the radius outward and it is a shockwave. One component, built once, generates a whole scene's worth of geometry — and a graphic built from one form that keeps returning reads as *designed*, where a graphic built from five unrelated decorations reads as assembled.

Reach for it when a graphic needs structure and the reflex answer is a card, a panel, or a blurred orb. It sits behind or around a figure and gives the frame architecture without adding a second idea.

## Markup

An SVG with an empty group the build populates. **Each tick is a \`<line>\` inside its own \`<g>\`**: the \`<g>\` carries the layout rotation as an SVG \`transform\` attribute, and the \`<line>\` is left free for GSAP. This separation is the whole trick — see Pitfalls.

\`\`\`html
<div class="absolute inset-0 flex items-center justify-center p-24">
  <svg id="rts-dial" class="overflow-visible" viewBox="0 0 400 400" width="520" height="520">
    <g id="rts-ring"></g>
    <circle cx="200" cy="200" r="14" fill="var(--l5-color-1, #6b7280)"/>
  </svg>
</div>
\`\`\`

## Timeline code

The build is a plain index-derived loop — deterministic, so it seeks cleanly. \`svgOrigin\` pins each tick's scale origin to its inner end (in viewBox units) so ticks grow *outward* rather than from their own midpoint.

\`\`\`js
const tl = gsap.timeline({ paused: true });

const CX = 200, CY = 200;
const TICK_COUNT = 48;
const R_INNER = 118;
const R_OUTER = 168;

const ring = document.querySelector('#rts-ring');
const SVG_NS = 'http://www.w3.org/2000/svg';
for (let i = 0; i < TICK_COUNT; i++) {
    const spoke = document.createElementNS(SVG_NS, 'g');
    spoke.setAttribute('transform', \`rotate(\${(i / TICK_COUNT) * 360} \${CX} \${CY})\`);
    const tick = document.createElementNS(SVG_NS, 'line');
    tick.setAttribute('x1', String(CX));
    tick.setAttribute('y1', String(CY - R_INNER));
    tick.setAttribute('x2', String(CX));
    tick.setAttribute('y2', String(CY - R_OUTER));
    tick.setAttribute('stroke', 'var(--l5-color-1, #6b7280)');
    tick.setAttribute('stroke-width', '6');
    tick.setAttribute('stroke-linecap', 'round');
    tick.classList.add('rts-tick');
    spoke.appendChild(tick);
    ring.appendChild(spoke);
}

gsap.set('.rts-tick', { svgOrigin: \`\${CX} \${CY - R_INNER}\` });

// Radial stagger — propagates from 12 o'clock symmetrically down both sides, so the ring
// closes at 6 o'clock. Index order would sweep one way round and read as a wipe.
const STEP = 0.012;
tl.fromTo('.rts-tick', { scaleY: 0, opacity: 0 }, {
    scaleY: 1, opacity: 1, duration: 0.4, ease: 'power3.out',
    stagger: (i) => Math.min(i, TICK_COUNT - i) * STEP,
}, 0.2);

window.__timelines = { main: tl };
\`\`\`

Two motions worth adding on top, both on \`ease: 'none'\` because constant velocity is unreadable as an event and keeps a long hold alive (see \`${SKILL_NAMES.sustainedSceneReveal}\`):

\`\`\`js
// Slow rotation of the whole ring. The group owns no per-tick transform, so GSAP can have it.
gsap.set('#rts-ring', { svgOrigin: \`\${CX} \${CY}\` });
tl.fromTo('#rts-ring', { rotation: 0 }, { rotation: 14, duration: 6, ease: 'none' }, 1.0);

// Disperse — ticks fly outward and fade, leaving the centre. A clean way out of the geometry.
tl.to('.rts-tick', {
    y: -46, opacity: 0, duration: 0.5, ease: 'power2.in',
    stagger: (i) => Math.min(i, TICK_COUNT - i) * 0.008,
}, 7.4);
\`\`\`

## Adapt

Every family member below is the same loop with different parameters. Pick one per graphic and let it recur.

- **Sunburst / rays** — \`TICK_COUNT\` 16–28, \`stroke-width\` 3–5, \`R_OUTER - R_INNER\` large (100px+ in viewBox units). Reads as emanation, energy, a source.
- **Gauge arc** — restrict the sweep: \`rotate((i / TICK_COUNT) * 270 - 135 ...)\`, and reveal ticks in plain index order rather than radially. The progressive tick reveal *is* the arc sweep, and it gives the arc a texture that a \`stroke-dashoffset\` draw does not.
- **Dial** — \`TICK_COUNT\` 40–64, short ticks (30–50px), and a marker tick at the value's index in the accent while the rest sit in a muted neutral. This is the encoding to reach for when the figure is a *rank* or a *position*, not a magnitude — see \`${SKILL_NAMES.dataAsMetaphor}\`.
- **Clock / dotted ring** — \`stroke-linecap: round\` with \`stroke-width\` equal to the tick length, which renders each tick as a dot.
- **Two-tone split** — colour by side (\`i < TICK_COUNT / 2\`) for a before/after or a ratio, with the split angle carrying the value.
- **Concentric field** — run the loop two or three times at different radii, and stagger the rings outward. Expanding rings under a counting figure is a strong pairing with \`${SKILL_NAMES.counterTickUp}\`.
- **Spiral** — grow the radius with the index (\`R_INNER + i * 2.4\`) and let the angle wrap past 360°. Slowly unwinding a spiral is legitimately hypnotic and buys a few seconds of held frame that conveys nothing but rhythm.
- **Tick length variation** — vary \`R_OUTER\` by \`i % 4 === 0 ? long : short\` for a measure-mark rhythm. Index-derived, so still deterministic.
- **Two-state shimmer** during a hold: yoyo \`scaleY\` between 1 and ~1.3 on alternate ticks. Finite half-cycles, even play count.

## Pitfalls

- **❗ The layout rotation and the animated transform must live on different elements.** GSAP writes SVG transforms into the element's own \`transform\` attribute, so tweening a tick that already carries \`rotate(...)\` clobbers the layout and every tick collapses to 12 o'clock. The \`<g>\` holds \`rotate\`; the \`<line>\` holds GSAP. Same class of trap as \`${SKILL_NAMES.svgIconEnrichment}\`'s off-centre clock hand.
- **Use \`svgOrigin\`, not CSS \`transform-origin\`.** CSS interprets the origin in the element's own bbox-local coordinates, so for a thin \`<line>\` a viewBox coordinate lands outside the line and the tick scales along an off-axis path. \`svgOrigin\` takes viewBox units, which is what you have.
- **Index order sweeps, radial order blooms.** GSAP's default stagger walks the DOM, which for this loop means one full turn round the dial. That reads as a clock wiping, not a form appearing. Use the symmetric \`Math.min(i, TICK_COUNT - i)\` stagger unless the sweep is the intent.
- **Keep the count modest.** 48 ticks is comfortable; a few hundred animated nodes costs real render time for no visible gain. For a dense field, draw it flat and animate the container.
- **The stagger tail is real**: \`STEP\` 0.012 across 48 ticks is ~0.29s before the duration is added. Check the ring finishes inside its window.
- **One radial system per graphic.** Two rings at different radii doing different things is the busy failure — a family member recurring is the point, two competing families is not.
- Ticks are GSAP-tweened on \`scaleY\`/\`opacity\`/\`y\` — never bake a CSS \`transform\` onto \`.rts-tick\`.
- \`overflow: visible\` on the SVG so ticks and dispersal aren't clipped at the viewBox edge; if the geometry deliberately runs past the canvas, the container also wants \`data-l5-allow-offscreen\`.
- A ring around body copy can wreck contrast at the point where a tick crosses a letter. Keep the ring's inner radius clear of the text, or drop the ring's opacity.`,
};
